import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { authAPI, rolesAPI, BACKEND_URL } from '../services/api';

const API = BACKEND_URL + '/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * AuthContext also carries the current user's effective RBAC permissions
 * (fetched from `/api/me/permissions` on login + hydration). Components use
 * `<Can perm="cases.edit">…</Can>` or `hasPerm('cases.edit')` to gate UI.
 * Legacy admin users are super-admin (`is_super_admin`) and always pass.
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [perms, setPerms] = useState({
    permissions: [],
    role_name: null,
    role_key: null,
    is_super_admin: false,
  });
  const [loading, setLoading] = useState(true);

  const hydratePerms = useCallback(async () => {
    try {
      const r = await rolesAPI.mePermissions();
      const p = {
        permissions: r.data?.permissions || [],
        role_name: r.data?.role_name || null,
        role_key: r.data?.role_key || null,
        is_super_admin: !!r.data?.is_super_admin,
      };
      setPerms(p);
      try { localStorage.setItem('permissions', JSON.stringify(p)); } catch { /* ignore */ }
    } catch (_) { /* if the perms fetch fails we leave perms empty; UI will hide guarded controls */ }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const savedUser = localStorage.getItem('user');
    const savedPerms = localStorage.getItem('permissions');
    if (token && savedUser) {
      setUser(JSON.parse(savedUser));
      if (savedPerms) {
        try { setPerms(JSON.parse(savedPerms)); } catch { /* ignore */ }
      }
      // Always refresh perms in the background — role could have changed
      // server-side while the browser was closed.
      hydratePerms();
    }
    setLoading(false);
  }, [hydratePerms]);

  const login = async (credentials) => {
    const response = await authAPI.login(credentials);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    // Fetch permissions right after login so route guards can decide
    // before any protected page renders.
    await hydratePerms();
    return userData;
  };

  const registerCandidate = async (data) => {
    const response = await authAPI.registerCandidate(data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('access_token', access_token);
    localStorage.setItem('user', JSON.stringify(userData));
    setUser(userData);
    await hydratePerms();
    return userData;
  };

  const logout = () => {
    try {
      const token = localStorage.getItem('access_token');
      if (token && user?.role === 'field_agent') {
        axios.post(
          `${API}/agent/shift/end`,
          {},
          { headers: { Authorization: `Bearer ${token}` } },
        ).catch(() => {});
      }
    } catch (_) { /* ignore */ }
    localStorage.removeItem('access_token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
    setUser(null);
    setPerms({ permissions: [], role_name: null, role_key: null, is_super_admin: false });
  };

  const hasPerm = useCallback((perm) => {
    if (!user) return false;
    if (perms.is_super_admin) return true;
    if (!perm) return true;
    return (perms.permissions || []).includes(perm);
  }, [user, perms]);

  const hasAnyPerm = useCallback((...list) => {
    if (!user) return false;
    if (perms.is_super_admin) return true;
    const set = new Set(perms.permissions || []);
    return list.some((p) => set.has(p));
  }, [user, perms]);

  const value = {
    user,
    loading,
    login,
    registerCandidate,
    logout,
    refreshPermissions: hydratePerms,
    permissions: perms.permissions,
    roleName: perms.role_name,
    roleKey: perms.role_key,
    isSuperAdmin: perms.is_super_admin,
    hasPerm,
    hasAnyPerm,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isCompany: user?.role === 'company',
    isCandidate: user?.role === 'candidate',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
